import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Shield,
  LogOut,
  Wallet,
  Activity,
  RefreshCw,
  ArrowLeft,
  Eye,
  EyeOff,
  Clock,
  Copy,
  Check,
  Trophy,
} from "lucide-react";
import { adminLogin, fetchAdminStats } from "../lib/api";

const STORAGE_KEY = "pokerealms_admin_pwd";

const Admin = () => {
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    document.title = "PokéRealms · Admin";
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      setPassword(saved);
      tryLogin(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tryLogin = async (pwd) => {
    setError(null);
    setLoading(true);
    try {
      await adminLogin(pwd);
      sessionStorage.setItem(STORAGE_KEY, pwd);
      setAuthed(true);
      await loadStats(pwd);
    } catch (e) {
      setError("Invalid password");
      sessionStorage.removeItem(STORAGE_KEY);
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async (pwd) => {
    setLoading(true);
    try {
      const data = await fetchAdminStats(pwd);
      setStats(data);
    } catch (e) {
      setError("Failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setPassword("");
    setStats(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    tryLogin(password);
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const shortAddr = (addr) =>
    addr ? `${addr.slice(0, 6)}…${addr.slice(-6)}` : "—";

  const formatDuration = (totalSeconds) => {
    const s = Math.max(0, Math.floor(totalSeconds || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${sec}s`;
    return `${sec}s`;
  };

  const copyAddress = async (addr) => {
    try {
      await navigator.clipboard.writeText(addr);
      setCopied(addr);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // ignore
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0b1626] flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/60 hover:text-[#ffcb05] mb-8 text-sm transition-colors"
          >
            <ArrowLeft size={16} /> Back to site
          </Link>

          <div className="border border-white/10 rounded-2xl p-8 bg-white/[0.03] backdrop-blur-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-[#ffcb05]/15 border border-[#ffcb05]/30 flex items-center justify-center">
                <Shield className="text-[#ffcb05]" size={20} />
              </div>
              <h1 className="pixel-font text-[#ffcb05] text-sm tracking-wider">
                ADMIN ACCESS
              </h1>
            </div>
            <p className="text-white/60 text-sm mb-6">
              Enter the admin password to view player statistics.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  autoFocus
                  className="w-full bg-[#0b1626] border border-white/10 rounded-md px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-[#ffcb05]/50 pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              {error && <p className="text-sm text-red-400">{error}</p>}

              <button
                type="submit"
                disabled={loading || !password}
                className="press-start pixel-font w-full text-xs py-3 rounded disabled:opacity-50"
              >
                {loading ? "VERIFYING…" : "UNLOCK"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b1626] text-white">
      {/* Top bar */}
      <div className="border-b border-white/10 bg-[#070f1c]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="text-[#ffcb05]" size={18} />
            <span className="pixel-font text-[#ffcb05] text-sm tracking-wider">
              POKÉREALMS · ADMIN
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => loadStats(password)}
              disabled={loading}
              className="px-3 py-2 rounded-md border border-white/10 hover:border-[#ffcb05]/50 hover:bg-white/5 flex items-center gap-2 text-sm text-white/70 hover:text-white"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={handleLogout}
              className="px-3 py-2 rounded-md border border-white/10 hover:border-red-400/50 hover:bg-red-400/5 flex items-center gap-2 text-sm text-white/70 hover:text-red-300"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="admin-dashboard">
        {/* KPIs — connected wallets only */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <StatCard
            icon={<Wallet size={18} />}
            label="Unique Wallets"
            value={stats?.unique_wallets ?? 0}
            testid="kpi-unique-wallets"
          />
          <StatCard
            icon={<Activity size={18} />}
            label="Wallet Sessions"
            value={stats?.wallet_sessions ?? 0}
            testid="kpi-wallet-sessions"
          />
          <StatCard
            icon={<Clock size={18} />}
            label="Total Play Time"
            value={formatDuration(stats?.total_seconds)}
            testid="kpi-total-time"
          />
        </div>

        {/* Wallets leaderboard — ranked by time spent in game */}
        <section className="mb-12">
          <h2 className="pixel-font text-[#ffcb05] text-xs tracking-wider mb-4 flex items-center gap-2">
            <Trophy size={14} /> TOP PLAYERS BY TIME
          </h2>
          <div className="border border-white/10 rounded-xl overflow-hidden bg-white/[0.02]">
            <table className="w-full text-sm" data-testid="wallets-table">
              <thead className="bg-white/[0.04] text-white/60 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium text-center w-12">#</th>
                  <th className="px-4 py-3 font-medium">Wallet Address</th>
                  <th className="px-4 py-3 font-medium">Time in Game</th>
                  <th className="px-4 py-3 font-medium text-center">Sessions</th>
                  <th className="px-4 py-3 font-medium">First Seen</th>
                  <th className="px-4 py-3 font-medium">Last Seen</th>
                </tr>
              </thead>
              <tbody>
                {(stats?.wallets ?? []).length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-white/40">
                      No wallets connected yet.
                    </td>
                  </tr>
                )}
                {(stats?.wallets ?? []).map((w, i) => (
                  <tr
                    key={w.wallet_address}
                    className="border-t border-white/5 hover:bg-white/[0.02]"
                    data-testid={`wallet-row-${i}`}
                  >
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`pixel-font text-[11px] ${
                          i === 0
                            ? "text-[#ffcb05]"
                            : i === 1
                            ? "text-white/80"
                            : i === 2
                            ? "text-[#cd7f32]"
                            : "text-white/40"
                        }`}
                      >
                        {i + 1}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[#ab9ff2]" title={w.wallet_address}>
                          {shortAddr(w.wallet_address)}
                        </span>
                        <button
                          onClick={() => copyAddress(w.wallet_address)}
                          title="Copy full address"
                          data-testid={`copy-wallet-${i}`}
                          className="text-white/40 hover:text-[#ffcb05] transition-colors"
                        >
                          {copied === w.wallet_address ? (
                            <Check size={14} className="text-green-400" />
                          ) : (
                            <Copy size={14} />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 text-[#ffcb05] font-semibold">
                        <Clock size={13} /> {formatDuration(w.total_seconds)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded bg-white/5 text-white/70 text-xs font-semibold">
                        {w.session_count}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white/70">{formatDate(w.first_seen)}</td>
                    <td className="px-4 py-3 text-white/70">{formatDate(w.last_seen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
};

const StatCard = ({ icon, label, value, testid }) => (
  <div className="border border-white/10 rounded-xl p-5 bg-white/[0.03]" data-testid={testid}>
    <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-wider mb-3">
      {icon}
      <span>{label}</span>
    </div>
    <div className="pixel-font text-[#ffcb05] text-2xl">{value}</div>
  </div>
);

export default Admin;
